import { Box, Typography } from '@dtx-company/design-system/src'
import { FC, ReactNode, useContext } from 'react'
import { Plan, PlanPurchaseExperienceMode } from '@dtx-company/true-common/src/types/Plan'
import { PlanPurchaseExperienceContext } from '@dtx-company/app/shared/common/src/components/PlanPurchaseExperience/PlanPurchaseExperienceContext'
import { PlanType } from '@dtx-company/true-common/src/types/planType'
import { PurchaseExperiencePartnerLogoList } from '@dtx-company/app/shared/common/src/components/PurchaseExperiencePartnerLogoList'
import Image from 'next/image'
import chroma from 'chroma-js'

function PromoHeading({ children }: { children: ReactNode }): JSX.Element {
  return (
    <Typography
      component="p"
      variant={{ xs: 'subtitleSmall', md: 'headlineXSmall', lg: 'subtitleSmall' }}
      fontWeight={600}
      textAlign={'center'}
    >
      {children}
    </Typography>
  )
}

function getPromoBackground(backgroundImageUrl?: string): {
  backgroundColor?: string
  backgroundImage?: string
} {
  if (chroma.valid(backgroundImageUrl)) {
    return { backgroundColor: backgroundImageUrl }
  }
  return {
    backgroundImage: backgroundImageUrl
      ? `url('${backgroundImageUrl}')`
      : `url('/images/pages/upgrade/promo-bg.svg')`
  }
}

export interface PromoCodeSectionProps {
  promoBackgroundImageUrl?: string
  promoHeading?: ReactNode
  selectedPlan: Plan | undefined
  mode?: PlanPurchaseExperienceMode
  promoAsset?: ReactNode
  promoHideCompanies?: boolean
}

export const PromoCodeSection: FC<PromoCodeSectionProps> = ({
  promoBackgroundImageUrl,
  promoHeading,
  selectedPlan,
  mode,
  promoAsset,
  promoHideCompanies
}) => {
  const { isSwitchingBillingCycle } = useContext(PlanPurchaseExperienceContext)
  const heading =
    promoHeading ??
    getDefaultPromoHeading({
      selectedPlanType: selectedPlan?.type,
      isSwitchingBillingCycle,
      isChangePaymentMode: mode === PlanPurchaseExperienceMode.CHANGE_PAYMENT
    })

  return (
    <Box
      component="aside"
      sx={theme => ({
        display: 'flex',
        flexFlow: 'column nowrap',
        flex: '0 0 auto',
        order: { xs: 1, lg: 2 },
        width: { lg: theme.spacing(94) }
      })}
    >
      <Box
        sx={theme => ({
          display: 'flex',
          flexFlow: 'column nowrap',
          justifyContent: { xs: 'flex-end', lg: 'flex-start' },
          flex: '1 0 auto',
          position: { lg: 'relative' },
          width: { lg: theme.spacing(94) },
          height: { xs: promoHeading ? 'auto' : theme.spacing(38), lg: 'auto' },
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          overflow: 'hidden',
          ...getPromoBackground(promoBackgroundImageUrl)
        })}
      >
        <Box
          component="header"
          sx={{
            display: { xs: 'none', lg: 'block' },
            p: 8.5
          }}
        >
          <PromoHeading>{heading}</PromoHeading>
        </Box>

        {promoHeading && (
          <Box
            sx={{
              display: { xs: 'block', lg: 'none' },
              px: 8.5,
              pt: { xs: 6, lg: 12 },
              pb: { xs: 6, lg: 4 }
            }}
          >
            <PromoHeading>{promoHeading}</PromoHeading>
          </Box>
        )}

        {!promoHeading && (
          <Box
            sx={theme => ({
              width: theme.spacing(56.5),
              height: theme.spacing(34),
              position: 'relative',
              margin: '0 auto',
              display: { xs: 'block', lg: 'none' }
            })}
          >
            <Image
              src={
                promoBackgroundImageUrl === '#242424'
                  ? '/graphics/lockModal/fc-pro-code-limit3-small-2x.png'
                  : '/images/pages/upgrade/pearl-promo-small-2x.png'
              }
              layout="fill"
              alt="Example Flowcode and Flowpage promo"
            />
          </Box>
        )}

        <Box
          sx={theme => ({
            display: { xs: 'none', lg: 'block' },
            margin: theme.spacing(12.5, 'auto', 0),
            maxWidth: '100%'
          })}
        >
          {promoAsset ?? (
            <Image
              src="/images/pages/upgrade/pearl-promo-2x.png"
              layout="intrinsic"
              width="321"
              height="334"
              alt="Example of a pretend company called Pearl, showing a custom flowcode in dark blue with a double-lined border and a custom flowpage with their logo, ways to contact them, google reviews, a video, and one of their products."
            />
          )}
        </Box>

        {!promoHideCompanies && (
          <Box
            component="footer"
            sx={{
              display: { xs: 'none', lg: 'block' },
              px: 8.5,
              mt: 8,
              width: '100%'
            }}
          >
            <PurchaseExperiencePartnerLogoList planType={selectedPlan?.type || PlanType.PRO} />
          </Box>
        )}
      </Box>
    </Box>
  )
}

export function getDefaultPromoHeading({
  selectedPlanType,
  isSwitchingBillingCycle,
  isChangePaymentMode
}: {
  selectedPlanType: PlanType | undefined
  isSwitchingBillingCycle: boolean
  isChangePaymentMode: boolean
}): string {
  if (isSwitchingBillingCycle || isChangePaymentMode) return ''
  switch (selectedPlanType) {
    case PlanType.PRO_PLUS_ORG:
      return 'Team collaboration, more data insights, more codes and Flowcode tools to level up your connections.'
    case PlanType.PRO_FLEX:
      return 'The only Flowcode plan you need for scaling real QR growth.'
    default:
      return 'Elevate your QR experience with more Flowcodes, Flowpages, advanced customization, and analytics'
  }
}
